/** 季 / 集展示 · 0 = 不分季（整项目一季） */

export function resolveEpisodesPerSeason(stored: number, episodeTarget: number): number {
  const target = Math.max(1, episodeTarget)
  if (stored <= 0 || stored >= target) return target
  return stored
}

export function usesSeasonSplit(stored: number, episodeTarget: number): boolean {
  const target = Math.max(1, episodeTarget)
  if (stored <= 0) return false
  return stored < target
}

export function seasonIndex(
  globalEpisodeNo: number,
  perSeason: number,
): number {
  if (globalEpisodeNo < 1) return 1
  const span = Math.max(1, perSeason)
  return Math.floor((globalEpisodeNo - 1) / span) + 1
}

export function episodeInSeasonIndex(
  globalEpisodeNo: number,
  perSeason: number,
): number {
  if (globalEpisodeNo < 1) return 1
  const span = Math.max(1, perSeason)
  return ((globalEpisodeNo - 1) % span) + 1
}

export function formatGlobalEpisode(n: number): string {
  return `E${String(n).padStart(2, '0')}`
}

export function formatSeasonEpisode(
  globalEpisodeNo: number,
  perSeason: number,
): string {
  const s = seasonIndex(globalEpisodeNo, perSeason)
  const e = episodeInSeasonIndex(globalEpisodeNo, perSeason)
  return `S${String(s).padStart(2, '0')}E${String(e).padStart(2, '0')}`
}

export function formatEpisodeLabel(
  globalEpisodeNo: number,
  storedPerSeason: number,
  episodeTarget: number,
): string {
  if (!usesSeasonSplit(storedPerSeason, episodeTarget)) {
    return formatGlobalEpisode(globalEpisodeNo)
  }
  return formatSeasonEpisode(globalEpisodeNo, resolveEpisodesPerSeason(storedPerSeason, episodeTarget))
}

/** 写作区连续滚动：渲染全集（跨季），避免季末无法滚入下一季 */
export function resolveEditorEpisodeNos(episodeTarget: number): number[] {
  const target = Math.max(1, episodeTarget)
  return Array.from({ length: target }, (_, i) => i + 1)
}

/** 多剧本：根据 listed 锚点判断 episodeNo 属于哪份剧本 */
export function resolveScriptSlot(episodeNo: number, listedScriptNos: number[]): number {
  const sorted = [...listedScriptNos].sort((a, b) => a - b)
  if (sorted.length === 0) return Math.max(1, episodeNo)
  if (sorted.length === 1) return sorted[0]!
  let slot = sorted[0]!
  for (const listed of sorted) {
    if (episodeNo >= listed) slot = listed
  }
  return slot
}

/** 多剧本：当前剧本拥有的 DB episode_no 列表（锚点 .. 下一份剧本锚点-1） */
export function resolveScriptEpisodeRange(
  scriptSlot: number,
  listedScriptNos: number[],
  episodeTarget: number,
  loadedDbEpisodeNos: number[] = [],
): number[] {
  const sorted = [...listedScriptNos].sort((a, b) => a - b)
  if (sorted.length <= 1) {
    return resolveEditorEpisodeNos(episodeTarget)
  }
  const idx = sorted.indexOf(scriptSlot)
  if (idx < 0) return [Math.max(1, scriptSlot)]
  const nextListed = sorted[idx + 1]
  if (nextListed != null) {
    const count = Math.max(1, nextListed - scriptSlot)
    return Array.from({ length: count }, (_, i) => scriptSlot + i)
  }
  const maxLoaded = loadedDbEpisodeNos
    .filter((n) => n >= scriptSlot)
    .reduce((max, n) => Math.max(max, n), scriptSlot)
  const count = Math.max(1, maxLoaded - scriptSlot + 1)
  return Array.from({ length: count }, (_, i) => scriptSlot + i)
}

export function toRelativeEpisodeNo(dbEpisodeNo: number, scriptSlot: number): number {
  return dbEpisodeNo - scriptSlot + 1
}

/** 剧本内相对集号 → SxxExx / Exx（不沿用全局 DB episode_no） */
export function formatEpisodeLabelForScript(
  dbEpisodeNo: number,
  scriptSlot: number,
  storedPerSeason: number,
  episodeCountInScript: number,
): string {
  const relative = toRelativeEpisodeNo(dbEpisodeNo, scriptSlot)
  return formatEpisodeLabel(relative, storedPerSeason, episodeCountInScript)
}

export function formatGlobalEpisodeForScript(dbEpisodeNo: number, scriptSlot: number): string {
  return formatGlobalEpisode(toRelativeEpisodeNo(dbEpisodeNo, scriptSlot))
}

export function usesMultiScriptMode(listedScriptNos: number[]): boolean {
  return listedScriptNos.length > 1
}

/** 撰写时「添加一季」：未分季则把当前集数锁成每季集数，并新增下一季第 1 集。 */
export function planAddSeason(
  storedPerSeason: number,
  episodeCount: number,
): { episodes_per_season: number; episode_target: number } {
  const count = Math.max(1, episodeCount)
  if (!usesSeasonSplit(storedPerSeason, count)) {
    return {
      episodes_per_season: count,
      episode_target: count + 1,
    }
  }
  const perSeason = resolveEpisodesPerSeason(storedPerSeason, count)
  const lastSeason = seasonIndex(count, perSeason)
  return {
    episodes_per_season: perSeason,
    episode_target: lastSeason * perSeason + 1,
  }
}

export function groupEpisodesBySeason<T extends { episode_no: number }>(
  items: T[],
  storedPerSeason: number,
  episodeTarget: number,
): { seasonNo: number; items: T[] }[] {
  const perSeason = resolveEpisodesPerSeason(storedPerSeason, episodeTarget)
  if (!usesSeasonSplit(storedPerSeason, episodeTarget)) {
    return [{ seasonNo: 1, items }]
  }
  const map = new Map<number, T[]>()
  for (const item of items) {
    const s = seasonIndex(item.episode_no, perSeason)
    const list = map.get(s) ?? []
    list.push(item)
    map.set(s, list)
  }
  return [...map.entries()]
    .sort(([a], [b]) => a - b)
    .map(([seasonNo, seasonItems]) => ({ seasonNo, items: seasonItems }))
}
